<template>
  <div class="checkout-page">
    <!-- Payment Processing Overlay -->
    <div v-if="paymentProcessing" class="payment-overlay">
      <div class="payment-overlay-content">
        <div class="payment-spinner"></div>
        <h3>Processing Payment...</h3>
        <p>Please wait while we connect to the payment gateway.</p>
        <p class="payment-note">Do not close this page.</p>
        <button @click="cancelPaymentOverlay" class="btn-cancel-payment">
          <i class="fas fa-times"></i> Cancel
        </button>
      </div>
    </div>
    <div class="container">
      <div class="checkout-header">
        <h1>Checkout</h1>
      </div>
      <div class="checkout-grid">
        <!-- Shipping Information -->
        <div class="checkout-form">
          <div class="form-section">
            <div class="section-header">
              <h3 class="section-title">Shipping Information</h3>
              <button
                v-if="user && user.id && !editMode"
                type="button"
                class="btn-edit"
                @click="enableEdit"
              >
                <i class="fas fa-edit"></i> Edit
              </button>
              <div v-if="user && user.id && editMode" class="edit-actions">
                <button
                  type="button"
                  class="btn-save-address"
                  @click="saveAddress"
                  :disabled="savingAddress"
                >
                  <i v-if="savingAddress" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-check"></i> Save
                </button>
                <button
                  type="button"
                  class="btn-cancel-edit"
                  @click="cancelEdit"
                >
                  <i class="fas fa-times"></i> Cancel
                </button>
              </div>
            </div>

            <form @submit.prevent="handleSubmit">
              <fieldset :disabled="checkoutLocked" style="border:0;padding:0;margin:0;">
              <div v-if="checkoutLocked" class="error" style="margin-bottom:12px;">
                Redirecting to payment. Please do not refresh or click back.
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>First Name *</label>
                  <input
                    v-model="form.first_name"
                    type="text"
                    placeholder="First name"
                    required
                    :readonly="user && user.id && !editMode"
                  >
                  <span v-if="errors.first_name" class="error">{{ errors.first_name[0] }}</span>
                </div>

                <div class="form-group">
                  <label>Last Name *</label>
                  <input
                    v-model="form.last_name"
                    type="text"
                    placeholder="Last name"
                    required
                    :readonly="user && user.id && !editMode"
                  >
                  <span v-if="errors.last_name" class="error">{{ errors.last_name[0] }}</span>
                </div>
              </div>

              <div class="form-group">
                <label>Email Address *</label>
                <input
                  v-model="form.email"
                  type="email"
                  placeholder="your@email.com"
                  required
                  :readonly="user && user.id && !editMode"
                >
                <span v-if="errors.email" class="error">{{ errors.email[0] }}</span>
              </div>

              <div class="form-group">
                <label>Phone Number *</label>
                <input
                  v-model="form.mobile"
                  type="tel"
                  placeholder="080xxxxxxxx"
                  required
                  :readonly="user && user.id && !editMode"
                >
                <span v-if="errors.mobile" class="error">{{ errors.mobile[0] }}</span>
              </div>

              <div class="form-group">
                <label>Address *</label>
                <textarea
                  v-model="form.shipping_address"
                  placeholder="Enter your full address"
                  rows="3"
                  required
                  :readonly="user && user.id && !editMode"
                ></textarea>
                <span v-if="errors.shipping_address" class="error">{{ errors.shipping_address[0] }}</span>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>City *</label>
                  <input
                    v-model="form.shipping_city"
                    type="text"
                    placeholder="City"
                    required
                    :readonly="user && user.id && !editMode"
                  >
                  <span v-if="errors.shipping_city" class="error">{{ errors.shipping_city[0] }}</span>
                </div>

                <div class="form-group">
                  <label>State *</label>
                  <select v-model="form.shipping_state_id" required :disabled="user && user.id && !editMode">
                    <option value="">Select State</option>
                    <option v-for="state in states" :key="state.id" :value="state.id">
                      {{ state.name }}
                    </option>
                  </select>
                  <span v-if="errors.shipping_state_id" class="error">{{ errors.shipping_state_id[0] }}</span>
                </div>
              </div>

              <div class="form-group">
                <label>Postal Code</label>
                <input
                  v-model="form.shipping_zip"
                  type="text"
                  placeholder="Postal code"
                  :readonly="user && user.id && !editMode"
                >
              </div>

              <div class="form-group">
                <label>Additional Notes</label>
                <textarea
                  v-model="form.notes"
                  placeholder="Any special delivery instructions?"
                  rows="2"
                ></textarea>
              </div>

              <!-- Account Creation for Guests Only -->
              <div class="form-section" v-if="!user || !user.id">
                <h3>Create Account</h3>
                <p class="account-creation-note">Create an account to track your order and enjoy faster checkout next time.</p>

                <div class="form-group">
                  <label>Password *</label>
                  <input
                    v-model="form.password"
                    type="password"
                    placeholder="Create a password (min 8 characters)"
                    required
                    minlength="8"
                  >
                  <span v-if="errors.password" class="error">{{ errors.password[0] }}</span>
                  <small class="form-hint">Password must be at least 8 characters</small>
                </div>

                <div class="form-group">
                  <label>Confirm Password *</label>
                  <input
                    v-model="form.password_confirmation"
                    type="password"
                    placeholder="Confirm your password"
                    required
                    minlength="8"
                  >
                  <span v-if="errors.password_confirmation" class="error">{{ errors.password_confirmation[0] }}</span>
                </div>
              </div>

              <div class="form-section">
                <div class="section-header">
                  <h3 class="section-title">Shipping Method</h3>
                </div>
                <div class="form-group">
                  <select v-model="form.shipping_method_id" required :class="{ 'input-error': shippingMethodError }">
                    <option value="">Select Shipping Method</option>
                    <option v-for="method in shippingMethods" :key="method.id" :value="method.id">
                      {{ method.name }}{{ method.carrier ? ' - ' + method.carrier : '' }}{{ method.max_delivery_days ? ' (' + method.max_delivery_days + ' days)' : '' }}
                    </option>
                  </select>
                  <span v-if="errors.shipping_method_id" class="error">{{ errors.shipping_method_id[0] }}</span>
                  <span v-if="shippingMethodError" class="error">Please select a shipping method</span>
                </div>
              </div>

              <div class="form-section">
                <div class="section-header">
                  <h3 class="section-title">Payment Method</h3>
                </div>
                <div v-if="enabledPaymentMethods.length === 0" class="no-payment-methods">
                  <p>No payment methods available. Please contact support.</p>
                </div>
                <div v-else class="payment-methods">
                  <label
                    v-for="method in enabledPaymentMethods"
                    :key="method.code"
                    class="payment-method"
                    :class="{ active: form.payment_method === method.code }"
                  >
                    <input
                      type="radio"
                      v-model="form.payment_method"
                      :value="method.code"
                      required
                      :disabled="checkoutLocked"
                    >
                    <div class="payment-method-content">
                      <i :class="getPaymentMethodIcon(method)"></i>
                      <span>{{ method.name }}</span>
                      <small>{{ method.description }}</small>
                    </div>
                  </label>
                </div>
                <span v-if="errors.payment_method" class="error">{{ errors.payment_method[0] }}</span>

                <!-- Bank Transfer Details -->
                <div v-if="selectedPaymentMethod && selectedPaymentMethod.type === 'manual'" class="bank-transfer-section">
                  <div class="bank-details-card">
                    <h3><i class="fas fa-university"></i> Bank Transfer Details</h3>
                    <p class="bank-instruction">Please transfer the exact amount to the account below:</p>

                    <div class="bank-info">
                      <div v-if="selectedPaymentMethod.bank_name" class="bank-row">
                        <span class="label">Bank Name:</span>
                        <strong>{{ selectedPaymentMethod.bank_name }}</strong>
                      </div>
                      <div v-if="selectedPaymentMethod.account_name" class="bank-row">
                        <span class="label">Account Name:</span>
                        <strong>{{ selectedPaymentMethod.account_name }}</strong>
                      </div>
                      <div v-if="selectedPaymentMethod.account_number" class="bank-row">
                        <span class="label">Account Number:</span>
                        <strong>{{ selectedPaymentMethod.account_number }}</strong>
                        <button type="button" @click="copyToClipboard(selectedPaymentMethod.account_number)" class="btn-copy" title="Copy">
                          <i class="fas fa-copy"></i>
                        </button>
                      </div>
                      <div class="bank-row amount-row">
                        <span class="label">Amount to Pay:</span>
                        <strong class="amount">₦{{ formatPrice(total) }}</strong>
                      </div>
                    </div>

                    <p v-if="selectedPaymentMethod.instructions" class="bank-instructions">
                      {{ selectedPaymentMethod.instructions }}
                    </p>

                    <div class="upload-section">
                      <label class="upload-label">
                        <i class="fas fa-cloud-upload-alt"></i>
                        Upload Payment Proof (Screenshot/Receipt)
                      </label>
                      <div class="upload-input-wrapper">
                        <input
                          type="file"
                          ref="paymentProofInput"
                          @change="handlePaymentProofUpload"
                          accept="image/*,.pdf"
                          :disabled="checkoutLocked"
                        >
                        <div v-if="!paymentProofFile" class="upload-placeholder" @click="$refs.paymentProofInput.click()">
                          <i class="fas fa-image"></i>
                          <span>Click to upload or drag and drop</span>
                          <small>PNG, JPG, or PDF (max 5MB)</small>
                        </div>
                        <div v-else class="upload-preview">
                          <img v-if="paymentProofPreview" :src="paymentProofPreview" alt="Payment proof">
                          <div v-else class="file-info">
                            <i class="fas fa-file-pdf"></i>
                            <span>{{ paymentProofFile.name }}</span>
                          </div>
                          <button type="button" @click="removePaymentProof" class="btn-remove-file">
                            <i class="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                      <span v-if="errors.payment_proof" class="error">{{ errors.payment_proof[0] }}</span>
                    </div>

                    <div class="bank-checkbox">
                      <label>
                        <input type="checkbox" v-model="bankTransferConfirmed" :disabled="checkoutLocked">
                        <span>I confirm that I have made the bank transfer</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <button type="submit" class="btn-submit" :disabled="loading || !form.shipping_method_id || !form.payment_method || (selectedPaymentMethod?.type === 'manual' && !bankTransferConfirmed)">
                <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-lock"></i>
                {{ loading ? 'Processing...' : 'Proceed to Payment' }}
              </button>
              </fieldset>
            </form>
          </div>
        </div>

        <!-- Order Summary -->
        <div class="order-summary">
          <h2>Order Summary</h2>

          <!-- Coupon Section -->
          <div class="coupon-section">
            <h4>Have a Coupon?</h4>
            <div v-if="!appliedCoupon" class="coupon-input-group">
              <input
                v-model="couponCode"
                type="text"
                placeholder="Enter coupon code"
                :disabled="applyingCoupon || checkoutLocked"
                class="coupon-input"
                @keyup.enter="applyCoupon"
              >
              <button
                @click="applyCoupon"
                :disabled="!couponCode || applyingCoupon || checkoutLocked"
                class="btn-apply-coupon"
              >
                <i v-if="applyingCoupon" class="fas fa-spinner fa-spin"></i>
                <span v-else>Apply</span>
              </button>
            </div>

            <div v-else class="applied-coupon">
              <div class="coupon-badge">
                <i class="fas fa-tag"></i>
                <span>{{ appliedCoupon.code }}</span>
                <button @click="removeCoupon" class="btn-remove-coupon" title="Remove coupon" :disabled="checkoutLocked">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <p class="coupon-savings">
                You're saving ₦{{ formatPrice(discountAmount) }}!
              </p>
            </div>
          </div>

          <div class="summary-items">
            <div v-for="item in cart.cart_items" :key="item.id" class="summary-item">
              <div class="item-info">
                <img :src="item.product.main_image || '/images/placeholder.jpg'" :alt="item.product.name">
                <div>
                  <h4>{{ item.product.name }}</h4>
                  <p>Qty: {{ item.quantity }}</p>
                </div>
              </div>
              <div class="item-price">
                ₦{{ formatPrice(getItemUnitPrice(item) * item.quantity) }}
                <span v-if="getItemOldPrice(item)" class="old-price">₦{{ formatPrice(getItemOldPrice(item) * item.quantity) }}</span>
              </div>
            </div>
          </div>

          <div class="summary-totals">
            <div class="summary-row">
              <span>Subtotal</span>
              <span>₦{{ formatPrice(subtotal) }}</span>
            </div>

            <div v-if="discountAmount > 0" class="summary-row discount">
              <span>Discount <span v-if="appliedCoupon" class="coupon-code">({{ appliedCoupon.code }})</span></span>
              <span>-₦{{ formatPrice(discountAmount) }}</span>
            </div>

            <div class="summary-row">
              <span>Shipping</span>
              <span v-if="calculatingShipping">
                <i class="fas fa-spinner fa-spin"></i> Calculating...
              </span>
              <span v-else>
                {{ shipping > 0 ? '₦' + formatPrice(shipping) : 'Select state & method' }}
              </span>
            </div>
            <div class="summary-row total">
              <span>Total</span>
              <span>₦{{ formatPrice(total) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="checkout-footer">
        <a href="/cart" class="btn-back-to-cart">
          <i class="fas fa-arrow-left"></i> Back to Cart
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Checkout',
  props: {
    cart: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    states: {
      type: Array,
      default: () => []
    },
    shippingMethods: {
      type: Array,
      default: () => []
    },
    paymentMethods: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      form: {
        first_name: this.user?.first_name || '',
        last_name: this.user?.last_name || '',
        email: this.user?.email || '',
        mobile: this.user?.mobile || '',
        shipping_address: this.user?.address || '',
        shipping_city: this.user?.city || '',
        shipping_state_id: this.user?.state_id || '',
        shipping_zip: this.user?.zip || '',
        shipping_method_id: '',
        payment_method: '',
        notes: '',
        password: '',
        password_confirmation: '',
        create_account: true // Always create account for guests
      },
      editMode: false, // Track if user is editing their info
      bankTransferConfirmed: false, // Track if user confirmed bank transfer
      paymentProofFile: null, // File object for payment proof
      paymentProofPreview: null, // Preview URL for image files
      savingAddress: false, // Track if address is being saved
      originalFormData: {}, // Store original data for cancel functionality
      flutterwaveLoaded: false,
      errors: {},
      loading: false,
      calculatedShipping: null,
      calculatingShipping: false,
      paystackPublicKey: null,
      flutterwavePublicKey: null,
      couponCode: '',
      appliedCoupon: null,
      applyingCoupon: false,
      diagnostics: {
        paystackScriptLoaded: false,
        paystackScriptLoadError: null,
        flutterwaveScriptLoaded: false,
        flutterwaveScriptLoadError: null
      },
      checkoutLocked: false,
      paymentProcessing: false,
      currentOrder: null,
      shippingMethodError: false
    }
  },
  computed: {
    enabledPaymentMethods() {
      // Filter to only show enabled payment methods (is_enabled = true or 1)
      return this.paymentMethods.filter(method => method.is_enabled === true || method.is_enabled === 1)
    },
    selectedPaymentMethod() {
      return this.enabledPaymentMethods.find(m => m.code === this.form.payment_method)
    },
    subtotal() {
      return this.cart.cart_items.reduce((total, item) => {
        return total + (this.getItemUnitPrice(item) * item.quantity)
      }, 0)
    },
    discountAmount() {
      if (!this.appliedCoupon) return 0

      const coupon = this.appliedCoupon
      let discount = 0

      if (coupon.type === 'percentage') {
        discount = (this.subtotal * coupon.value) / 100
      } else {
        discount = coupon.value
      }

      // Ensure discount doesn't exceed cart total
      return Math.min(discount, this.subtotal)
    },
    shipping() {
      return this.calculatedShipping || 0
    },
    total() {
      return this.subtotal - this.discountAmount + this.shipping
    }
  },
  mounted() {
    // Get Paystack public key from payment methods prop (from database)
    const paystackMethod = this.paymentMethods.find(m => m.code === 'paystack')
    this.paystackPublicKey = paystackMethod?.public_key || ''

    // Get Flutterwave public key from payment methods prop (from database)
    const flutterwaveMethod = this.paymentMethods.find(m => m.code === 'flutterwave')
    this.flutterwavePublicKey = flutterwaveMethod?.public_key || ''

    // Load Flutterwave script
    this.loadFlutterwaveScript()

    // Check for applied coupon
    this.checkCoupon()
  },
  watch: {
    'form.shipping_state_id': 'calculateShipping',
    'form.shipping_method_id': 'calculateShipping'
  },
  methods: {
    enableEdit() {
      // Store original data in case user cancels
      this.originalFormData = { ...this.form }
      this.editMode = true
    },

    cancelEdit() {
      // Restore original data
      this.form = { ...this.originalFormData }
      this.editMode = false
    },

    async saveAddress() {
      this.savingAddress = true
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const response = await fetch('/onboarding/address', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            mobile: this.form.mobile,
            address: this.form.shipping_address,
            city: this.form.shipping_city,
            state_id: this.form.shipping_state_id,
            zip: this.form.shipping_zip
          })
        })

        if (response.ok) {
          this.editMode = false
          this.originalFormData = { ...this.form }
          if (window.Swal) {
            window.Swal.fire({
              icon: 'success',
              title: 'Address Updated',
              text: 'Your address has been saved successfully.',
              timer: 2000,
              showConfirmButton: false
            })
          }
        } else {
          const data = await response.json()
          if (data.errors) {
            this.errors = data.errors
          }
          throw new Error(data.message || 'Failed to save address')
        }
      } catch (error) {
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.message || 'Failed to save address. Please try again.',
            confirmButtonColor: '#F55203'
          })
        }
      } finally {
        this.savingAddress = false
      }
      this.errors = {}
    },

    formatPrice(price) {
      return new Intl.NumberFormat('en-NG').format(price)
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        if (window.Swal) {
          window.Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: 'Account number copied to clipboard',
            timer: 1500,
            showConfirmButton: false
          })
        }
      })
    },

    handlePaymentProofUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.errors.payment_proof = ['File size must be less than 5MB']
        return
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']
      if (!allowedTypes.includes(file.type)) {
        this.errors.payment_proof = ['Please upload an image (JPG, PNG, GIF, WebP) or PDF file']
        return
      }

      this.paymentProofFile = file
      this.errors.payment_proof = null

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.paymentProofPreview = e.target.result
        }
        reader.readAsDataURL(file)
      } else {
        this.paymentProofPreview = null
      }
    },

    removePaymentProof() {
      this.paymentProofFile = null
      this.paymentProofPreview = null
      if (this.$refs.paymentProofInput) {
        this.$refs.paymentProofInput.value = ''
      }
    },

    getPaymentMethodIcon(method) {
      // Return appropriate icon class based on payment method code or type
      const icons = {
        'paystack': 'fas fa-credit-card',
        'flutterwave': 'fas fa-wallet',
        'bank_transfer': 'fas fa-university',
        'stripe': 'fab fa-stripe'
      }

      if (icons[method.code]) {
        return icons[method.code]
      }

      // Fallback based on type
      if (method.type === 'manual') {
        return 'fas fa-money-bill-wave'
      }

      return 'fas fa-credit-card'
    },

    getItemUnitPrice(item) {
      return Number(
        item?.variant?.base_price ??
        item?.product?.base_price ??
        item?.price ??
        0
      )
    },

    getItemOldPrice(item) {
      const basePrice = Number(item?.variant?.base_price ?? item?.product?.base_price ?? 0)
      const comparePrice = Number(item?.variant?.compare_price ?? item?.product?.compare_price ?? 0)

      if (comparePrice > basePrice && basePrice > 0) return comparePrice
      return null
    },

    async checkCoupon() {
      try {
        const response = await fetch('/cart/summary', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          }
        })

        const data = await response.json()
        if (data.coupon) {
          this.appliedCoupon = data.coupon
        }
      } catch (error) {
        // Coupon check failed silently
      }
    },

    async applyCoupon() {
      if (!this.couponCode) return

      this.applyingCoupon = true

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const formData = new FormData()
        formData.append('_token', csrfToken)
        formData.append('code', this.couponCode.toUpperCase())

        const response = await fetch('/cart/coupon/apply', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          body: formData
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.appliedCoupon = data.coupon || this.appliedCoupon

          if (window.Swal) {
            window.Swal.fire({
              icon: 'success',
              title: 'Coupon Applied!',
              text: data.message || 'Discount applied successfully',
              timer: 2000,
              showConfirmButton: false,
              position: 'top-end',
              toast: true
            })
          }
          return
        }

        // Handle API error response
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Invalid Coupon',
            text: data.message || 'This coupon code is not valid',
            confirmButtonColor: '#F55203'
          })
        } else {
          alert(data.message || 'Failed to apply coupon')
        }
      } catch (error) {
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Invalid Coupon',
            text: error.message || 'This coupon code is not valid',
            confirmButtonColor: '#F55203'
          })
        } else {
          alert(error.message || 'Failed to apply coupon')
        }
      } finally {
        this.applyingCoupon = false
      }
    },

    async removeCoupon() {
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const formData = new FormData()
        formData.append('_token', csrfToken)

        const response = await fetch('/cart/coupon/remove', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          body: formData
        })

        const data = await response.json()

        if (data.success) {
          this.appliedCoupon = null
          this.couponCode = ''

          if (window.Swal) {
            window.Swal.fire({
              icon: 'info',
              title: 'Coupon Removed',
              text: 'Discount has been removed',
              timer: 1500,
              showConfirmButton: false,
              position: 'top-end',
              toast: true
            })
          }
        } else {
          throw new Error(data.message || 'Failed to remove coupon')
        }
      } catch (error) {
        // Remove coupon failed silently
      }
    },

    async calculateShipping() {
      if (!this.form.shipping_state_id || !this.form.shipping_method_id) {
        this.calculatedShipping = null
        return
      }

      this.calculatingShipping = true

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

        const response = await fetch('/checkout/calculate-shipping', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            state_id: this.form.shipping_state_id,
            shipping_method_id: this.form.shipping_method_id
          })
        })

        const data = await response.json()

        if (response.ok) {
          this.calculatedShipping = data.shipping_cost
        } else {
          this.calculatedShipping = null

          // If backend reports validation/cart issues, show that instead of shipping-rate message
          const errorText = data?.message || data?.error || 'Unable to calculate shipping.'
          if (window.Swal) {
            window.Swal.fire({
              icon: 'error',
              title: 'Shipping Not Available',
              text: data.debug
                ? `${errorText} (state_id: ${data.debug.state_id}, method_id: ${data.debug.shipping_method_id})`
                : errorText,
              confirmButtonColor: '#F55203'
            })
          }
        }
      } catch (error) {
        this.calculatedShipping = null
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Shipping Calculation Failed',
            text: 'Unable to calculate shipping at the moment. Please try again.',
            confirmButtonColor: '#F55203'
          })
        }
      } finally {
        this.calculatingShipping = false
      }
    },

    loadFlutterwaveScript() {
      // Check if already loaded
      if (window.FlutterwaveCheckout) {
        this.flutterwaveLoaded = true
        this.diagnostics.flutterwaveScriptLoaded = true
        return Promise.resolve()
      }

      if (document.getElementById('flutterwave-script')) {
        // Script exists but may still be loading
        return new Promise((resolve) => {
          const checkLoaded = setInterval(() => {
            if (window.FlutterwaveCheckout) {
              clearInterval(checkLoaded)
              this.flutterwaveLoaded = true
              this.diagnostics.flutterwaveScriptLoaded = true
              resolve()
            }
          }, 100)
          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkLoaded)
            if (!window.FlutterwaveCheckout) {
              this.diagnostics.flutterwaveScriptLoadError = 'Script load timeout'
            }
            resolve()
          }, 10000)
        })
      }

      return new Promise((resolve) => {
        const script = document.createElement('script')
        script.id = 'flutterwave-script'
        script.src = 'https://checkout.flutterwave.com/v3.js'
        script.async = true
        script.onload = () => {
          this.flutterwaveLoaded = true
          this.diagnostics.flutterwaveScriptLoaded = true
          resolve()
        }
        script.onerror = () => {
          this.diagnostics.flutterwaveScriptLoadError = 'Failed to load script'
          resolve()
        }
        document.head.appendChild(script)
      })
    },

    async handleSubmit() {
      if (this.checkoutLocked) {
        return
      }

      if (this.calculatingShipping) {
        return
      }

      if (this.calculatedShipping === null) {
        if (window.Swal) {
          window.Swal.fire({
            icon: 'warning',
            title: 'Shipping Required',
            text: 'Please select a state and shipping method with an available shipping rate.',
            confirmButtonColor: '#F55203'
          })
        }
        return
      }

      // Validate shipping method is selected
      if (!this.form.shipping_method_id) {
        this.shippingMethodError = true
        if (window.Swal) {
          window.Swal.fire({
            icon: 'warning',
            title: 'Shipping Method Required',
            text: 'Please select a shipping method',
            confirmButtonColor: '#F55203'
          })
        }
        return
      }
      this.shippingMethodError = false

      // Validate payment method is selected
      if (!this.form.payment_method) {
        if (window.Swal) {
          window.Swal.fire({
            icon: 'warning',
            title: 'Payment Method Required',
            text: 'Please select a payment method',
            confirmButtonColor: '#F55203'
          })
        }
        return
      }

      // Validate password for guest account creation
      if (!this.user || !this.user.id) {
        if (!this.form.password) {
          if (window.Swal) {
            window.Swal.fire({
              icon: 'warning',
              title: 'Password Required',
              text: 'Please enter a password for your account',
              confirmButtonColor: '#F55203'
            })
          }
          return
        }
        if (this.form.password !== this.form.password_confirmation) {
          if (window.Swal) {
            window.Swal.fire({
              icon: 'warning',
              title: 'Password Mismatch',
              text: 'Password and confirmation do not match',
              confirmButtonColor: '#F55203'
            })
          }
          return
        }
      }

      // Validate bank transfer confirmation and proof
      if (this.selectedPaymentMethod?.type === 'manual') {
        if (!this.bankTransferConfirmed) {
          if (window.Swal) {
            window.Swal.fire({
              icon: 'warning',
              title: 'Confirmation Required',
              text: 'Please confirm that you have made the bank transfer',
              confirmButtonColor: '#F55203'
            })
          }
          return
        }
      }

      this.loading = true
      this.errors = {}

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

        // Use FormData if there's a payment proof file to upload
        let response
        if (this.paymentProofFile) {
          const formData = new FormData()
          Object.keys(this.form).forEach(key => {
            if (this.form[key] !== null && this.form[key] !== '') {
              formData.append(key, this.form[key])
            }
          })
          formData.append('payment_proof', this.paymentProofFile)
          formData.append('bank_transfer_confirmed', this.bankTransferConfirmed ? '1' : '0')

          response = await fetch('/orders', {
            method: 'POST',
            headers: {
              'X-CSRF-TOKEN': csrfToken,
              'Accept': 'application/json'
            },
            body: formData
          })
        } else {
          // Regular JSON request
          response = await fetch('/orders', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': csrfToken,
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              ...this.form,
              bank_transfer_confirmed: this.bankTransferConfirmed
            })
          })
        }

        const data = await response.json()

        if (response.ok) {
          // Update global auth state if new user was created
          if (data.user) {
            // Update window.authUser for header/cart
            window.authUser = data.user

            // Dispatch event to update Vue auth store
            window.dispatchEvent(new CustomEvent('auth-updated', {
              detail: { user: data.user }
            }))
          }

          // Initialize payment based on selected method
          const selectedMethod = this.paymentMethods.find(m => m.code === this.form.payment_method)

          if (selectedMethod && selectedMethod.type === 'manual') {
            // For manual payment (bank transfer), redirect to success page
            // Payment proof already uploaded, order awaiting admin confirmation
            window.location.href = `/orders/${data.order.number}/success`
          } else if (this.form.payment_method === 'paystack') {
            this.initializePaystackPayment(data.order)
          } else if (this.form.payment_method === 'flutterwave') {
            this.initializeFlutterwavePayment(data.order)
          } else {
            // Fallback - redirect to payment page
            window.location.href = `/orders/${data.order.number}/payment`
          }
        } else {
          if (data.errors) {
            this.errors = data.errors
          }

          // Handle requires_login response
          if (data.requires_login) {
            if (window.Swal) {
              window.Swal.fire({
                icon: 'info',
                title: 'Account Exists',
                html: data.message,
                showCancelButton: true,
                confirmButtonText: 'Login',
                cancelButtonText: 'Use Different Email',
                confirmButtonColor: '#F55203'
              }).then((result) => {
                if (result.isConfirmed) {
                  window.location.href = '/login'
                }
              })
            }
          } else {
            if (window.Swal) {
              window.Swal.fire({
                icon: 'error',
                title: 'Error',
                text: data.message || data.error || 'Failed to create order. Please try again.',
                confirmButtonColor: '#F55203'
              })
            }
          }
          this.loading = false
        }
      } catch (error) {
        this.loading = false

        // Try to get error message from response
        let errorMessage = 'An error occurred. Please try again.'
        if (error.response) {
          try {
            const errorData = await error.response.json()
            errorMessage = errorData.message || errorData.error || errorMessage
          } catch (e) {
            // Could not parse error response
          }
        }

        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage,
            confirmButtonColor: '#F55203'
          })
        }
      }
    },

    async ensurePaystackLoaded() {
      return new Promise((resolve, reject) => {
        // Check if already loaded
        if (window.PaystackPop) {
          resolve()
          return
        }

        // Check if script tag exists
        let script = document.querySelector('script[src*="paystack"]')

        if (!script) {
          script = document.createElement('script')
          script.src = 'https://js.paystack.co/v1/inline.js'
          script.async = true
          document.head.appendChild(script)
        }

        // Listen for load
        script.onload = () => {
          resolve()
        }

        script.onerror = () => {
          reject(new Error('Failed to load Paystack script'))
        }

        // Timeout after 10 seconds
        setTimeout(() => {
          if (!window.PaystackPop) {
            reject(new Error('Paystack script load timeout'))
          }
        }, 10000)
      })
    },

    async initializePaystackPayment(order) {
      // Validate public key
      if (!this.paystackPublicKey) {
        window.Swal.fire({
          icon: 'error',
          title: 'Configuration Error',
          text: 'Paystack public key is not configured. Please contact support.',
          confirmButtonColor: '#F55203'
        })
        this.loading = false
        return
      }

      try {
        // Ensure Paystack is loaded
        await this.ensurePaystackLoaded()

        if (!window.PaystackPop) {
          throw new Error('PaystackPop not available after script load')
        }

        // Store order and show overlay
        this.currentOrder = order
        this.paymentProcessing = true

        const handler = window.PaystackPop.setup({
          key: this.paystackPublicKey,
          email: this.form.email,
          amount: order.total * 100, // Convert to kobo
          currency: 'NGN',
          ref: order.number + '-' + Date.now(),
          metadata: {
            order_id: order.id,
            order_number: order.number,
            customer_name: this.form.first_name + ' ' + this.form.last_name,
            customer_phone: this.form.mobile
          },
          callback: (response) => {
            this.handlePaymentSuccess(response, order)
          },
          onClose: () => {
            this.handlePaymentClosed(order)
          }
        })

        handler.openIframe()
        // Hide overlay once modal is open
        this.paymentProcessing = false

      } catch (error) {
        this.paymentProcessing = false

        const errorDetails = [
          `Error: ${error.message}`,
          `PaystackPop Available: ${!!window.PaystackPop}`,
          `Script in DOM: ${!!document.querySelector('script[src*="paystack"]')}`,
          `Public Key Set: ${!!this.paystackPublicKey}`
        ].join('\n')

        window.Swal.fire({
          icon: 'error',
          title: 'Payment Gateway Error',
          html: `<p>Failed to initialize payment gateway.</p><pre style="text-align: left; font-size: 11px; margin-top: 10px;">${errorDetails}</pre>`,
          confirmButtonColor: '#F55203'
        })
        this.loading = false
      }
    },

    async handlePaymentSuccess(response, order) {
      try {
        // Verify payment on server
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const verifyResponse = await fetch('/payment/callback/paystack?reference=' + response.reference, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken
          }
        })

        if (verifyResponse.ok || verifyResponse.redirected) {
          if (window.Swal) {
            window.Swal.fire({
              icon: 'success',
              title: 'Payment Successful!',
              text: 'Thank you for your order',
              timer: 2000,
              showConfirmButton: false
            })
          }
          setTimeout(() => {
            window.location.href = `/orders/${order.number}/success`
          }, 2000)
        } else {
          throw new Error('Payment verification failed')
        }
      } catch (error) {
        window.Swal.fire({
          icon: 'error',
          title: 'Verification Error',
          text: 'Please contact support with order number: ' + order.number,
          confirmButtonColor: '#F55203'
        })
      } finally {
        this.loading = false
      }
    },

    cancelPaymentOverlay() {
      this.paymentProcessing = false
      this.loading = false
      // Order is already created, redirect to order detail page
      if (this.currentOrder) {
        window.Swal.fire({
          icon: 'info',
          title: 'Payment Pending',
          text: 'Your order has been created. You can complete payment from your order page.',
          confirmButtonText: 'View Order',
          showCancelButton: true,
          cancelButtonText: 'Stay Here',
          confirmButtonColor: '#F55203'
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = `/orders/${this.currentOrder.number}`
          }
        })
      }
    },

    handlePaymentClosed(order) {
      this.paymentProcessing = false
      this.loading = false
      window.Swal.fire({
        icon: 'warning',
        title: 'Payment Cancelled',
        text: 'You can complete payment from your order page',
        confirmButtonText: 'View Order',
        confirmButtonColor: '#F55203'
      }).then(() => {
        window.location.href = `/orders/${order.number}`
      })
    },

    async initializeFlutterwavePayment(order) {
      // Store order and show overlay
      this.currentOrder = order
      this.paymentProcessing = true

      // Ensure script is loaded
      if (!window.FlutterwaveCheckout) {
        await this.loadFlutterwaveScript()
      }

      if (!window.FlutterwaveCheckout) {
        this.paymentProcessing = false
        window.Swal.fire({
          icon: 'error',
          title: 'Payment Error',
          text: 'Flutterwave payment gateway not loaded. Please refresh the page.',
          confirmButtonColor: '#F55203'
        })
        this.loading = false
        return
      }

      // Use the public key from payment methods (already loaded) or fallback to meta tag
      const publicKey = this.flutterwavePublicKey || document.querySelector('meta[name="flutterwave-public-key"]')?.content || ''

      if (!publicKey) {
        this.paymentProcessing = false
        window.Swal.fire({
          icon: 'error',
          title: 'Configuration Error',
          text: 'Payment gateway not configured. Please contact support.',
          confirmButtonColor: '#F55203'
        })
        this.loading = false
        return
      }

      // Hide overlay once modal opens
      this.paymentProcessing = false

      window.FlutterwaveCheckout({
        public_key: publicKey,
        tx_ref: order.number + '-' + Date.now(),
        amount: order.total,
        currency: 'NGN',
        payment_options: 'card,banktransfer,ussd',
        customer: {
          email: this.form.email,
          phone_number: this.form.mobile,
          name: this.form.first_name + ' ' + this.form.last_name,
        },
        customizations: {
          title: 'Order Payment',
          description: 'Payment for Order #' + order.number,
          logo: window.location.origin + '/logos/logodark.svg',
        },
        meta: {
          order_id: order.id,
          order_number: order.number,
          customer_name: this.form.first_name + ' ' + this.form.last_name,
        },
        callback: (response) => {
          this.handleFlutterwaveCallback(response, order)
        },
        onclose: () => {
          this.handlePaymentClosed(order)
        },
      })
    },

    async handleFlutterwaveCallback(response, order) {
      if (response.status === 'successful') {
        try {
          // Verify payment on server
          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
          const verifyResponse = await fetch('/payment/callback/flutterwave?transaction_id=' + response.transaction_id, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'X-CSRF-TOKEN': csrfToken
            }
          })

          if (verifyResponse.ok || verifyResponse.redirected) {
            if (window.Swal) {
              window.Swal.fire({
                icon: 'success',
                title: 'Payment Successful!',
                text: 'Thank you for your order',
                timer: 2000,
                showConfirmButton: false
              })
            }
            setTimeout(() => {
              window.location.href = `/orders/${order.number}/success`
            }, 2000)
          } else {
            throw new Error('Payment verification failed')
          }
        } catch (error) {
          window.Swal.fire({
            icon: 'error',
            title: 'Verification Error',
            text: 'Please contact support with order number: ' + order.number,
            confirmButtonColor: '#F55203'
          })
        } finally {
          this.loading = false
        }
      } else {
        this.loading = false
        window.Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: 'Your payment was not successful. Please try again.',
          confirmButtonColor: '#F55203'
        })
      }
    }
  }
}
</script>
